import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/query/queryKeys'
import { isApiConfigured } from '@/config/api/env'
import { decideProposal, fetchProposals } from '@/services/integrations'

/**
 * Write actions waiting on the user, across every conversation.
 *
 * The inline card in chat is the primary place to approve. This exists because
 * that card can scroll out of sight or sit in a thread the user has moved on
 * from, and an action nobody can find is an action that silently never happens.
 *
 * `staleTime` is short: a proposal may be approved from the chat card in the
 * same session, and this list showing it as still waiting would invite a second
 * click that the server would only reject with a 409.
 */
export function usePendingApprovals() {
  return useQuery({
    queryKey: queryKeys.integrations.proposals,
    queryFn: ({ signal }) => fetchProposals({ signal }),
    enabled: isApiConfigured(),
    staleTime: 10_000,
    select: (rows) => rows.filter((row) => row.status === 'pending'),
  })
}

export function useDecideProposal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ proposalId, approved }) => decideProposal({ proposalId, approved }),
    // Always refetch, including on failure: a 409 means something else already
    // settled it, so the list is out of date either way.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.proposals }),
  })
}
